import { CommonModule, JsonPipe } from '@angular/common';
import { Component, computed, effect, input, OnDestroy, OnInit, Optional, Signal, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MaterialModule } from 'src/app/shared/modules/material.module';
import { PageEvent } from '@angular/material/paginator';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ImportWizardService } from '../../../import-wizard.service';
import { ImportWizardStorage, typeOptions, ValidationErrorsInterface } from '../../../import-wizard.storage';
import { MatDialogRef } from '@angular/material/dialog';
import { ImportWizardComponent } from '../../../import-wizard.component';
import { ImportWizardTabularService } from '../import-wizard-tabular.service';
import { FormsModule } from '@angular/forms';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { ToastrService } from 'ngx-toastr';
import { NatService } from 'src/app/services/nat.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MapService } from 'src/app/services/map.service';
import { RepositoryService } from 'src/app/pages/project/map/repository/repository.service';
import { DatasetType, GetImportWizardErrorsItem } from 'src/app/dtos/import-wizard/import-wizard.dto';
import { ImportWizardPendingStorage } from '../../../components/pending-list/import-wizard-pending-list.storage';
import { ImportWizardPendingService } from '../../../components/pending-list/import-wizard-pending-list.service';
import { StatusPageComponent } from 'src/app/shared/status-page/status-page.component';
import { AuthKeycloakService } from 'src/app/services/auth-keycloak.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedImportsStorage } from 'src/app/pages/shared-imports/shared-imports.storage';
import { ImportWizardTabularValidationHandler } from '@/app/handlers/import-wizard/import-wizard-tabular-validation.handler';

export interface NatsImportWizardProgressResponseInterface { 
    status: string;
    rows_inserted_batch: number;
    cumulativa_total: number;
    total_expected: number;
    progress_percentage: number;
}

@Component({
    selector: 'app-import-wizard-tabular-confirmation_validation',
    templateUrl: './import-wizard-tabular-confirmation_validation.component.html',
    styleUrls: ['./import-wizard-tabular-confirmation_validation.component.scss'],
    providers: [ImportWizardTabularValidationHandler],
    imports: [
        CommonModule,
        MaterialModule,
        ScrollingModule,
        FormsModule,
        AlertComponent,
        StatusPageComponent
    ],
    standalone: true
})
export class ImportWizardTabularConfirmationValidationComponent implements OnInit, OnDestroy {
    file = input<File>();

    pagesSizes = { 
        'min': 5,
        'mid-lower': 10,
        'mid-upper': 25,
        'max': 100
    }
    
    tabularRows = computed(() => this.importWizardStorage.rows());
    tabularTableDataSource = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        const end = start + this.pageSize();

        return this.tabularRows().slice(start, end);
    });

    serviceColumns = computed(() => this.importWizardStorage.columns());
    serviceColumnsNames = computed(() => this.serviceColumns().map((col) => col.name));

    displayColumns = computed(() => this.importWizardStorage.displayColumns());
    displayColumnsNames = computed(() => this.importWizardStorage.display_columns_names());

    pageSize = signal(this.pagesSizes['mid-lower']);
    currentPage = signal(1);
    total = computed(() => this.importWizardStorage.validationErrors().total);

    _state = computed(() => this.importWizardStorage.state());

    validationErrors = signal<GetImportWizardErrorsItem[]>([]);
    groupedErrors = computed(() => {
        const map = new Map<string, any>();

        this.importWizardStorage.validationErrors().items.forEach(err => {
            if (!map.has(err.column_name)) {
                map.set(err.column_name, {
                    column_name: err.original_column,
                    expected_type: err.expected_type,
                    rows: [],
                    values: [],
                    is_invalid_file: err.is_invalid_file,
                    error_message: err.error_message
                });
            }

            const group = map.get(err.column_name);
            group.rows.push(err.row_number);
            group.values.push(err.value);
        });

        return Array.from(map.values());
    });

    showErrorsSectionTitle = computed(() =>
        this.groupedErrors().length > 0 &&
        !this.groupedErrors().some(e => e.is_invalid_file)
    );

    showDetailedErrors = signal<boolean>(false);

    toggleDetailedErrors() {
        this.showDetailedErrors.set(!this.showDetailedErrors());
    }

    private createRepositoryLoadingProgress$ = new BehaviorSubject<number>(0.00);
    createRepositoryLoadingProgress = this.createRepositoryLoadingProgress$.asObservable();

    isLoading = signal<boolean>(true);
    isFinishing: boolean = false;
    isAlertVisible = false;
    private progressInterval?: any;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private importWizardStorage: ImportWizardStorage,
        private importWizardPendingStorage: ImportWizardPendingStorage,
        private importWizardPendingService: ImportWizardPendingService,
        private importWizardService: ImportWizardService,
        private importWizardTabularService: ImportWizardTabularService,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>,
        private toastrService: ToastrService,
        private natsService: NatService,
        private mapService: MapService,
        private repositoryService: RepositoryService,
        private sharedImportsStorage: SharedImportsStorage,
        private validationHandler: ImportWizardTabularValidationHandler
    ) {
    }

    async ngOnInit() {
        await this.getImportWizardErrors();
        this.isLoading.set(false);
        this.subscribeToHandlerProgress();
        this.validationHandler.subscribeToImportProgress(this._state().id);
    }

    private subscribeToHandlerProgress() {
        this.validationHandler.progress$.subscribe(progress => {
            if (progress > this.createRepositoryLoadingProgress$.value || progress === 100) {
                this.stopProgressSimulation();
                this.createRepositoryLoadingProgress$.next(progress);
            }

            if (progress === 100) {
                if (!this.importWizardStorage.state().isShared) {
                    this.refetchRepositoryData();
                }

                this.toastrService.success('Importação concluida com sucesso!', '', {
                    closeButton: true
                });

                if (this.dialogRef) {
                    this.dialogRef.close();
                }

                if (this.importWizardStorage.state().isShared) {
                    this.handleRedirectToHub();
                    this.importWizardStorage.reset();
                }
            }
        });
    }

    async handlePageChange(event: PageEvent) {
        this.currentPage.set(event.pageIndex + 1);
        this.pageSize.set(event.pageSize);
        await this.getImportWizardErrors();
    }

    async handleButtonClick() {
        const state = this._state();

        this.isFinishing = true;
        this.startProgressSimulation();

        try {
            const result = await this.importWizardTabularService.finishImportWizard(state.id);
            if (result && result.table_name) {
                this.validationHandler.setTableName(result.table_name);
            }
        } catch (error) {
            this.stopProgressSimulation();
            this.createRepositoryLoadingProgress$.next(0);
            this.isFinishing = false;
            console.log(error);
            this.toastrService.error('Erro ao finalizar importação. Tente novamente.', '', {
                closeButton: true
            });
        }
    }

    private startProgressSimulation(): void {
        this.stopProgressSimulation();
        this.createRepositoryLoadingProgress$.next(0);

        this.progressInterval = setInterval(() => {
            const current = this.createRepositoryLoadingProgress$.value;
            if (current < 99) {
                const step = (99 - current) * 0.012;
                const next = current + Math.max(step, 0.01);
                this.createRepositoryLoadingProgress$.next(Math.min(next, 99));
            } else {
                this.stopProgressSimulation();
            }
        }, 1000);
    }

    private stopProgressSimulation(): void {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = undefined;
        }
    }

    getLabelByValue(value: string): string {
        return this.typeOptions.find(opt => opt.value === value.toLowerCase())!.label;
    }

    handleCancelClick(): void {
        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;
        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }


    private handleRedirectToHub() {
        const import_link = this.sharedImportsStorage.sharedImport()?.import_link ?? '/';

        const path = import_link.startsWith('https') || import_link.startsWith('http') 
            ? new URL(import_link).pathname 
            : import_link;

        this.router.navigateByUrl(path);
    }

    private async getImportWizardErrors() { 
        const data = await firstValueFrom(
            this.importWizardService.getImportWizardErrors(this.importWizardStorage.state().id, this.currentPage(), this.pageSize())
        );

        const validationErrorsData = {
            items: data.items,
            total: data.total
        } as ValidationErrorsInterface;

        this.importWizardStorage.setValidationErrors(validationErrorsData);
    }

    private refetchRepositoryData() {        

      this.mapService.getRepositoriesTablesList(this.repositoryService.selectedRepository().repository_id).subscribe(
        (success) => {
          this.repositoryService.setTabularTables(success);
        },
      );

      this.importWizardPendingService.useGetAllPendingImports(DatasetType.TABULAR).subscribe({
          next: (result) => {
              this.importWizardPendingStorage.setPendingImports(result);
          },
          error: (error) => console.error(error)
      });
    }

    async ngOnDestroy() {
        this.stopProgressSimulation();
    }

    pageSizesArray = Object.values(this.pagesSizes);
    typeOptions = typeOptions;
}